import { ApiError, Me, api } from './api/client';
import { pageFadeIn } from './ui/animations';
import { renderLogin } from './ui/login';
import { renderLobby, unmountLobby } from './ui/lobby';
import { mountMatchView, unmountMatchView } from './ui/match-view';
import { mountHelpButton } from './ui/patterns-help';
import { mountOceanBackground } from './ui/ocean-bg';
import { renderFriends, unmountFriends } from './ui/friends';
import { renderProfile, unmountProfile } from './ui/profile';
import { renderChallenges, unmountChallenges } from './ui/challenges';
import { renderCollection, unmountCollection } from './ui/collection';
import { renderForgotPassword, renderResetPassword, renderVerifyEmail } from './ui/forgot-password';

const root = document.getElementById('app');
if (!root) throw new Error('No #app');

mountOceanBackground();

type Route =
  | { name: 'login' }
  | { name: 'lobby' }
  | { name: 'match'; id: string }
  | { name: 'friends' }
  | { name: 'profile' }
  | { name: 'challenges' }
  | { name: 'collection' }
  | { name: 'forgot-password' }
  | { name: 'reset-password'; token: string }
  | { name: 'verify-email'; token: string };

let me: Me | null = null;

async function bootstrap(): Promise<void> {
  try {
    me = await api.me();
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      me = null;
    } else {
      // Network/other errors — show login regardless; surface error there.
      me = null;
    }
  }
  // Maintenance check: if the ops flag is up, every other route is gated.
  // Polled every 30s so a flip on the server takes effect mid-session.
  void startMaintenancePoll();
  routeFromUrl();
}


let maintenanceOverlayEl: HTMLDivElement | null = null;

async function startMaintenancePoll(): Promise<void> {
  const check = async () => {
    try {
      const res = await api.maintenanceStatus();
      if (res.active) showMaintenanceOverlay();
      else hideMaintenanceOverlay();
    } catch {
      // Don't disrupt the user if the endpoint is unreachable.
    }
  };
  await check();
  setInterval(check, 30_000);
}


function showMaintenanceOverlay(): void {
  if (maintenanceOverlayEl) return;
  const el = document.createElement('div');
  el.className = 'maintenance-overlay';
  el.innerHTML = `
    <div class="maintenance-card">
      <h1>⚓ Plunder is updating</h1>
      <p>We'll be back shortly. Your matches and stats are safe.</p>
    </div>`;
  document.body.appendChild(el);
  maintenanceOverlayEl = el;
}


function hideMaintenanceOverlay(): void {
  if (maintenanceOverlayEl) {
    maintenanceOverlayEl.remove();
    maintenanceOverlayEl = null;
  }
}

function routeFromUrl(): void {
  const hash = location.hash;
  // Public routes — usable without being logged in.
  if (hash.startsWith('#/forgot-password')) {
    go({ name: 'forgot-password' }, false);
    return;
  }
  const resetMatch = hash.match(/^#\/reset-password\?token=(.+)$/);
  if (resetMatch) {
    go({ name: 'reset-password', token: decodeURIComponent(resetMatch[1]) }, false);
    return;
  }
  const verifyMatch = hash.match(/^#\/verify-email\?token=(.+)$/);
  if (verifyMatch) {
    go({ name: 'verify-email', token: decodeURIComponent(verifyMatch[1]) }, false);
    return;
  }
  if (!me) {
    go({ name: 'login' }, false);
    return;
  }
  const matchHash = hash.match(/^#\/match\/(.+)$/);
  if (matchHash) {
    go({ name: 'match', id: matchHash[1] }, false);
  } else if (hash.startsWith('#/friends')) {
    go({ name: 'friends' }, false);
  } else if (hash.startsWith('#/profile')) {
    go({ name: 'profile' }, false);
  } else if (hash.startsWith('#/challenges')) {
    go({ name: 'challenges' }, false);
  } else if (hash.startsWith('#/collection')) {
    go({ name: 'collection' }, false);
  } else {
    go({ name: 'lobby' }, false);
  }
}

function go(route: Route, pushUrl = true): void {
  unmountMatchView();
  unmountLobby();
  unmountFriends();
  unmountProfile();
  unmountChallenges();
  unmountCollection();
  if (pushUrl) {
    const url =
      route.name === 'lobby'           ? '#/' :
      route.name === 'match'           ? `#/match/${route.id}` :
      route.name === 'friends'         ? '#/friends' :
      route.name === 'profile'         ? '#/profile' :
      route.name === 'challenges'      ? '#/challenges' :
      route.name === 'collection'      ? '#/collection' :
      route.name === 'forgot-password' ? '#/forgot-password' :
      route.name === 'reset-password'  ? `#/reset-password?token=${encodeURIComponent(route.token)}` :
      route.name === 'verify-email'    ? `#/verify-email?token=${encodeURIComponent(route.token)}` :
                                         '#/login';
    if (location.hash !== url) history.pushState({}, '', url);
  }
  if (route.name === 'login') {
    renderLogin(root!, async () => {
      try { me = await api.me(); } catch { me = null; }
      go({ name: 'lobby' });
    }, () => go({ name: 'forgot-password' }));
    pageFadeIn(root!);
    return;
  }
  if (route.name === 'lobby') {
    renderLobby(root!, me!,
      (id) => go({ name: 'match', id }),
      () => { me = null; go({ name: 'login' }); },
      () => go({ name: 'friends' }),
      () => go({ name: 'profile' }),
      () => go({ name: 'challenges' }),
      () => go({ name: 'collection' }));
    pageFadeIn(root!);
    return;
  }
  if (route.name === 'challenges') {
    renderChallenges(root!, () => go({ name: 'lobby' }));
    pageFadeIn(root!);
    return;
  }
  if (route.name === 'collection') {
    renderCollection(root!, () => go({ name: 'lobby' }));
    pageFadeIn(root!);
    return;
  }
  if (route.name === 'friends') {
    renderFriends(root!, () => go({ name: 'lobby' }), (id) => go({ name: 'match', id }));
    pageFadeIn(root!);
    return;
  }
  if (route.name === 'profile') {
    renderProfile(root!, () => go({ name: 'lobby' }));
    pageFadeIn(root!);
    return;
  }
  if (route.name === 'match') {
    mountMatchView(root!, route.id, () => go({ name: 'lobby' }));
    pageFadeIn(root!);
    return;
  }
  if (route.name === 'forgot-password') {
    renderForgotPassword(root!, () => go({ name: 'login' }));
    pageFadeIn(root!);
    return;
  }
  if (route.name === 'reset-password') {
    renderResetPassword(root!, route.token, () => go({ name: 'login' }));
    pageFadeIn(root!);
    return;
  }
  if (route.name === 'verify-email') {
    renderVerifyEmail(root!, route.token,
      async () => {
        try { me = await api.me(); } catch { me = null; }
        go({ name: me ? 'lobby' : 'login' });
      },
      () => go({ name: 'login' }));
    pageFadeIn(root!);
  }
}

window.addEventListener('popstate', routeFromUrl);

mountHelpButton();
bootstrap();
